import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const CustomFieldsModal = ({ isOpen, onClose, fieldType }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [editingField, setEditingField] = useState(null);

  const fieldConfig = {
    serviceTypes: {
      title: 'Service Types',
      table: 'ticket_service_types',
      icon: 'Wrench',
      description: 'Manage custom service type options for tickets'
    },
    statuses: {
      title: 'Statuses',
      table: 'ticket_statuses',
      icon: 'Activity',
      description: 'Manage custom status options for tickets'
    },
    priorities: {
      title: 'Priorities',
      table: 'ticket_priorities',
      icon: 'AlertCircle',
      description: 'Manage custom priority levels for tickets'
    }
  };

  const config = fieldConfig?.[fieldType] || fieldConfig?.serviceTypes;

  useEffect(() => {
    if (isOpen) {
      fetchFields();
    }
  }, [isOpen, fieldType]);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from(config?.table)?.select('*')?.order('display_order', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error) {
      console.error('Error fetching fields:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async () => {
    if (!newFieldName?.trim()) return;

    try {
      setLoading(true);
      const { data, error } = await supabase?.from(config?.table)?.insert({
          name: newFieldName,
          description: newFieldDescription,
          display_order: fields?.length || 0
        })?.select();

      if (error) throw error;

      setFields([...fields, ...(data || [])]);
      setNewFieldName('');
      setNewFieldDescription('');
    } catch (error) {
      console.error('Error adding field:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = async (id, updates) => {
    try {
      setLoading(true);
      const { error } = await supabase?.from(config?.table)?.update(updates)?.eq('id', id);

      if (error) throw error;

      setFields(fields?.map(f => f?.id === id ? { ...f, ...updates } : f));
      setEditingField(null);
    } catch (error) {
      console.error('Error updating field:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (id) => {
    if (!confirm('Are you sure you want to delete this field?')) return;

    try {
      setLoading(true);
      const { error } = await supabase?.from(config?.table)?.delete()?.eq('id', id);

      if (error) throw error;

      setFields(fields?.filter(f => f?.id !== id));
    } catch (error) {
      console.error('Error deleting field:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    await handleUpdateField(id, { is_active: !isActive });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={config?.icon} size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">
                Manage {config?.title}
              </h2>
              <p className="text-sm text-muted-foreground">{config?.description}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" />
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="text-sm font-caption font-medium text-foreground mb-3">
                Add New {config?.title?.slice(0, -1)}
              </h3>
              <div className="space-y-3">
                <Input
                  label="Name"
                  placeholder={`Enter ${config?.title?.toLowerCase()?.slice(0, -1)} name`}
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e?.target?.value)}
                />
                <Input
                  label="Description (Optional)"
                  placeholder="Enter description"
                  value={newFieldDescription}
                  onChange={(e) => setNewFieldDescription(e?.target?.value)}
                />
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleAddField}
                  disabled={!newFieldName?.trim() || loading}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add {config?.title?.slice(0, -1)}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-caption font-medium text-foreground mb-3">
                Existing {config?.title}
              </h3>
              {loading && fields?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Loader" size={24} color="currentColor" className="animate-spin mx-auto mb-2" />
                  Loading...
                </div>
              ) : fields?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Inbox" size={48} color="currentColor" className="mx-auto mb-2 opacity-50" />
                  <p>No {config?.title?.toLowerCase()} added yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fields?.map((field) => (
                    <div
                      key={field?.id}
                      className="flex items-center justify-between p-4 bg-card border border-border rounded-lg hover:bg-muted/30 transition-smooth"
                    >
                      {editingField === field?.id ? (
                        <div className="flex-1 flex gap-2">
                          <Input
                            value={field?.name}
                            onChange={(e) =>
                              setFields(fields?.map(f =>
                                f?.id === field?.id ? { ...f, name: e?.target?.value } : f
                              ))
                            }
                            className="flex-1"
                          />
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleUpdateField(field?.id, { name: field?.name })}
                            iconName="Check"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingField(null);
                              fetchFields();
                            }}
                            iconName="X"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground">{field?.name}</span>
                              {!field?.is_active && (
                                <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {field?.description && (
                              <p className="text-xs text-muted-foreground mt-1">{field?.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(field?.id, field?.is_active)}
                              iconName={field?.is_active ? 'Eye' : 'EyeOff'}
                              title={field?.is_active ? 'Deactivate' : 'Activate'}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingField(field?.id)}
                              iconName="Edit"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteField(field?.id)}
                              iconName="Trash2"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border">
          <Button variant="outline" fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomFieldsModal;
